import type { ProjectLaunchOrderData } from '@/components/Markdown/MarkdownViewer/CustomTagCom/ProjectLaunchOrder/const';
import type { LoginModalOpenOptions } from '@/stores/loginModal';
import {
  clearPendingPayBeforeLogin,
  getPendingPayBeforeLogin,
  type PendingPayBeforeLoginContext,
  savePendingPayBeforeLogin,
} from './payBeforeLoginStorage';

const ANONYMOUS_PAID_EMAIL_QUERY_TIMEOUT_MS = 5000;

type AnonymousPaymentStatus =
  | { status: 'paid'; email: string }
  | { status: 'unpaid' }
  | { status: 'unavailable'; error: unknown };

type AnonymousPayBeforeLoginAction = 'open-login' | 'open-cashier' | 'stop';

type QueryLatestPaidEmail = (
  timeoutMs: number,
) => Promise<{ email?: string | null }>;

type ResumeLaunchOrder = (
  projectId: string,
  order?: ProjectLaunchOrderData | null,
) => Promise<unknown>;

export function normalizeLatestPaidEmail(email: unknown): string | null {
  return typeof email === 'string' && email.trim()
    ? email.trim()
    : null;
}

export async function queryAnonymousPaymentStatus(
  queryLatestPaidEmail: QueryLatestPaidEmail,
): Promise<AnonymousPaymentStatus> {
  try {
    const { email } = await queryLatestPaidEmail(ANONYMOUS_PAID_EMAIL_QUERY_TIMEOUT_MS);
    const normalizedEmail = normalizeLatestPaidEmail(email);
    return normalizedEmail
      ? { status: 'paid', email: normalizedEmail }
      : { status: 'unpaid' };
  } catch (error) {
    return { status: 'unavailable', error };
  }
}

export function getAnonymousPayBeforeLoginAction(
  paymentStatus: AnonymousPaymentStatus,
  hasPendingPayment: boolean,
): AnonymousPayBeforeLoginAction {
  if (hasPendingPayment || paymentStatus.status === 'paid') {
    return 'open-login';
  }

  return paymentStatus.status === 'unpaid'
    ? 'open-cashier'
    : 'stop';
}

interface PayBeforeLoginModalInput {
  context: PendingPayBeforeLoginContext;
  initialEmail?: string;
  header?: LoginModalOpenOptions['header'];
  contextPersisted?: boolean;
}

interface PayBeforeLoginModalDependencies {
  savePending?: typeof savePendingPayBeforeLogin;
  openLogin: (options?: LoginModalOpenOptions) => void;
  resume: ResumeLaunchOrder;
}

export function openPayBeforeLoginModal(
  input: PayBeforeLoginModalInput,
  dependencies: PayBeforeLoginModalDependencies,
): void {
  const persisted = input.contextPersisted
    ?? (dependencies.savePending ?? savePendingPayBeforeLogin)(input.context);
  const onSuccess = persisted
    ? undefined
    : () => {
        void dependencies.resume(input.context.projectId, input.context.order);
      };
  const options: LoginModalOpenOptions = {
    initialEmail: input.initialEmail,
    header: input.header,
    onSuccess,
    dismissible: false,
  };

  dependencies.openLogin(options);
}

interface ConsumePendingPayBeforeLoginDependencies {
  getPending?: typeof getPendingPayBeforeLogin;
  clearPending?: typeof clearPendingPayBeforeLogin;
  resume: ResumeLaunchOrder;
}

export async function consumePendingPayBeforeLoginAfterLogin(
  dependencies: ConsumePendingPayBeforeLoginDependencies,
): Promise<boolean> {
  const pendingPayment = (dependencies.getPending ?? getPendingPayBeforeLogin)();
  if (!pendingPayment) {
    return false;
  }

  (dependencies.clearPending ?? clearPendingPayBeforeLogin)();
  try {
    await dependencies.resume(pendingPayment.projectId, pendingPayment.order);
  } catch (error) {
    console.warn('[PayBeforeLogin] Failed to resume launch order:', error);
  }
  return true;
}

export function getPendingPayBeforeLoginForProject(
  projectId: string,
): PendingPayBeforeLoginContext | null {
  const pendingPayment = getPendingPayBeforeLogin();
  return pendingPayment?.projectId === projectId
    ? pendingPayment
    : null;
}

interface AnonymousPaymentCompletionDependencies {
  queryLatestPaidEmail: QueryLatestPaidEmail;
  isLogin: () => boolean;
  savePending?: typeof savePendingPayBeforeLogin;
  openLogin: (options?: LoginModalOpenOptions) => void;
  resume: ResumeLaunchOrder;
}

export async function handleAnonymousPaymentCompletion(
  context: PendingPayBeforeLoginContext,
  dependencies: AnonymousPaymentCompletionDependencies,
): Promise<void> {
  const contextPersisted = (dependencies.savePending ?? savePendingPayBeforeLogin)(context);
  const resumeAfterLogin = async () => {
    if (contextPersisted) {
      await consumePendingPayBeforeLoginAfterLogin({ resume: dependencies.resume });
    } else {
      await dependencies.resume(context.projectId, context.order);
    }
  };

  if (dependencies.isLogin()) {
    await resumeAfterLogin();
    return;
  }

  const paymentStatus = await queryAnonymousPaymentStatus(dependencies.queryLatestPaidEmail);
  if (paymentStatus.status === 'unavailable') {
    console.warn('[PayBeforeLogin] Failed to query anonymous paid email:', paymentStatus.error);
  }

  if (dependencies.isLogin()) {
    await resumeAfterLogin();
    return;
  }

  openPayBeforeLoginModal(
    {
      context,
      contextPersisted,
      initialEmail: paymentStatus.status === 'paid'
        ? paymentStatus.email
        : undefined,
    },
    {
      openLogin: dependencies.openLogin,
      resume: dependencies.resume,
      savePending: dependencies.savePending,
    },
  );
}
